import { motion } from 'framer-motion'
import education from '@/content/education.json'
import experiences from '@/content/experiences.json'
import { Link, useLocation } from 'react-router-dom'
import { slugify } from '@/lib/slug'
import StatusPill, { type ItemStatus } from '@/components/ui/StatusPill'

type Edu = { slug?: string; school: string; degree: string; start: string; end: string; logo?: string; status?: ItemStatus }
type Exp = {
  slug?: string
  role: string
  company: string
  start: string
  end: string
  status?: ItemStatus
  bullets?: string[]
  logo?: string
}

type Props = {
  variant?: 'default' | 'lite'
}

function parseMonthYear(value: string): number {
  // expected: "MM-YYYY"
  const m = /^(\d{2})-(\d{4})$/.exec(value.trim())
  if (!m) return 0
  const month = Number(m[1])
  const year = Number(m[2])
  if (!Number.isFinite(month) || !Number.isFinite(year)) return 0
  return year * 12 + month
}

function formatMonthYear(value: string): string {
  const m = /^(\d{2})-(\d{4})$/.exec(value.trim())
  if (!m) return value
  return `${m[1]}/${m[2]}`
}

export default function Timeline({ variant = 'default' }: Props) {
  const edus = (education as Edu[]) || []
  const exps = (experiences as Exp[]) || []
  const location = useLocation()
  const eduItems = edus.map((e) => ({
    kind: 'edu' as const,
    title: `${e.degree} — ${e.school}`,
    period: `${formatMonthYear(e.start)} — ${formatMonthYear(e.end)}`,
    slug: e.slug ?? slugify(`${e.degree}-${e.school}`),
    routeKind: 'education' as const,
    status: e.status,
    sortKey: parseMonthYear(e.start) || parseMonthYear(e.end)
  }))
  const expItems = exps
    .map((e) => ({
      kind: 'exp' as const,
      title: `${e.role} — ${e.company}`,
      period: `${formatMonthYear(e.start)} — ${formatMonthYear(e.end)}`,
      slug: e.slug ?? slugify(`${e.role}-${e.company}`),
      routeKind: 'experience' as const,
      status: e.status,
      sortKey: parseMonthYear(e.start) || parseMonthYear(e.end)
    }))
    .sort((a, b) => b.sortKey - a.sortKey)
  const items = [...eduItems, ...expItems].sort((a, b) => b.sortKey - a.sortKey)
  const isLite = variant === 'lite'

  if (!isLite) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Parcours</h2>
        <ol className="relative border-l border-slate-800 pl-6 space-y-6">
          {items.map((it, i) => (
            <li key={i} className="group">
              <span className="absolute -left-1.5 mt-1 h-3 w-3 rounded-full bg-brand-500 ring-2 ring-slate-900" />
              <motion.div
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3 }}
                className="rounded-lg border border-slate-800 bg-slate-900/40 p-4"
              >
                <div className="text-sm uppercase tracking-wide text-slate-400">
                  {it.kind === 'edu' ? 'Formation' : 'Expérience'}
                </div>
                <div className="mt-2">
                  <StatusPill status={it.status} variant={variant} />
                </div>
                <div className="font-medium">{it.title}</div>
                <div className="text-sm text-slate-400">{it.period}</div>
                <div className="mt-2">
                  <Link
                    to={`/portfolio/${it.routeKind}/${it.slug}`}
                    state={{ backgroundLocation: location, variant }}
                    className="text-xs text-brand-400 hover:underline"
                  >
                    Voir le détail
                  </Link>
                </div>
              </motion.div>
            </li>
          ))}
        </ol>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-extrabold italic tracking-tight text-slate-100">Parcours</h2>

      {/* Expériences + formations */}
      <ol className="relative border-l border-slate-700/70 pl-6 space-y-6">
        {items.map((it, i) => (
          <li key={it.slug + i} className="group">
            <span className="absolute -left-1.5 mt-1 h-3 w-3 rounded-full bg-brand-500 ring-2 ring-slate-950" />
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.35 }}
              className="lite-card rounded-2xl p-5"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="text-xs tracking-widest uppercase text-slate-400">
                  {it.kind === 'edu' ? 'Formation' : 'Expérience'}
                </div>
                <StatusPill status={it.status} variant={variant} />
              </div>
              <div className="mt-1 font-semibold text-slate-100">{it.title}</div>
              <div className="text-sm text-slate-400">{it.period}</div>
              <div className="mt-2">
                <Link
                  to={`/portfolio/${it.routeKind}/${it.slug}`}
                  state={{ backgroundLocation: location, variant }}
                  className="text-xs font-semibold text-brand-300 hover:underline"
                >
                  Voir le détail
                </Link>
              </div>
            </motion.div>
          </li>
        ))}
      </ol>
    </div>
  )
}


